"use client"

import { Suspense, useState } from "react"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { SignedIn, SignedOut, UserButton, useAuth } from "@clerk/nextjs"
import { Menu, X, Coins } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useCustomer } from "@/lib/hooks/use-customer"

// Reads the current URL (path + search params) for the sign-in/sign-up
// redirect_url query. Must live in a child component wrapped in Suspense so
// the parent Header can prerender statically — useSearchParams() forces a
// client-side-render bailout unless its caller is Suspense-bounded.
function CurrentUrlReader({ onResolve }: { onResolve: (url: string) => React.ReactNode }) {
  const pathname = usePathname()
  const params = useSearchParams()
  const currentUrl = pathname + (params.toString() ? `?${params.toString()}` : "")
  return <>{onResolve(currentUrl)}</>
}

interface HeaderBodyProps {
  currentUrl: string
}

function HeaderBody({ currentUrl }: HeaderBodyProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { isLoaded } = useAuth()
  const { customer } = useCustomer()
  const videosRemaining = customer?.features?.['credits']?.balance ?? 0

  const scrollTo = (id: string) => {
    setIsMenuOpen(false)
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="container mx-auto px-5 h-16 flex items-center justify-between max-w-7xl">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 group">
          <img src="/favicon.svg" alt="Transcriptr" className="w-9 h-9 rounded-lg transition-transform group-hover:scale-105" />
          <span className="font-display text-xl font-bold text-white">Transcriptr</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <Link href="/transcript" className="text-sm font-medium text-[#ff6b4a] hover:text-[#CC3E21] transition-colors">
            TranscriptTool
          </Link>
          <button
            onClick={() => scrollTo("features")}
            className="text-sm font-medium text-white/60 hover:text-white transition-colors"
          >
            Features
          </button>
          <button
            onClick={() => scrollTo("pricing")}
            className="text-sm font-medium text-white/60 hover:text-white transition-colors"
          >
            Pricing
          </button>
          <Link href="/tools" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Tools
          </Link>
          <Link href="/guides" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Guides
          </Link>
          <Link href="/blog" className="text-sm font-medium text-white/60 hover:text-white transition-colors">
            Blog
          </Link>

          {isLoaded && (
            <>
              <SignedOut>
                <Link href={`/sign-in?redirect_url=${encodeURIComponent(currentUrl)}`} className="text-sm font-medium text-white/60 hover:text-white transition-colors">
                  Sign In
                </Link>
                <Link href={`/sign-up?redirect_url=${encodeURIComponent(currentUrl)}`}>
                  <Button className="bg-[#CC3E21] hover:bg-[#b5351b] text-white rounded-full px-6 h-9 font-semibold text-sm shadow-lg shadow-[#CC3E21]/20 hover:scale-105 transition-all">
                    Get Free Clips
                  </Button>
                </Link>
              </SignedOut>

              <SignedIn>
                <Link href="/home">
                  <Button variant="secondary" className="rounded-full px-5 font-semibold text-sm bg-white/[0.05] border border-white/[0.08] text-white hover:bg-white/[0.08]">
                    Dashboard
                  </Button>
                </Link>
                <UserButton afterSignOutUrl="/">
                  <UserButton.MenuItems>
                    <UserButton.Link
                      label={`${videosRemaining} Credits`}
                      labelIcon={<Coins className="h-4 w-4" />}
                      href="/pricing"
                    />
                  </UserButton.MenuItems>
                </UserButton>
              </SignedIn>
            </>
          )}
        </div>

        {/* Mobile: CTA + Hamburger */}
        <div className="flex md:hidden items-center gap-3">
          {isLoaded && (
            <>
              <SignedOut>
                <Link href={`/sign-up?redirect_url=${encodeURIComponent(currentUrl)}`}>
                  <Button size="sm" className="bg-[#CC3E21] hover:bg-[#b5351b] text-white rounded-full px-4 h-8 font-semibold text-xs">
                    Get Free Clips
                  </Button>
                </Link>
              </SignedOut>
              <SignedIn>
                <Link href="/home">
                  <Button size="sm" variant="secondary" className="rounded-full px-3 h-8 font-semibold text-xs bg-white/[0.05] border border-white/[0.08] text-white">
                    Dashboard
                  </Button>
                </Link>
              </SignedIn>
            </>
          )}
          <button className="text-white p-1" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      {isMenuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-white/[0.06] p-5 flex flex-col gap-4 animate-in slide-in-from-top-5">
          <Link href="/transcript" className="block py-2 text-[#ff6b4a] hover:text-[#CC3E21] font-medium" onClick={() => setIsMenuOpen(false)}>Transcript</Link>
          <button onClick={() => scrollTo("features")} className="block py-2 text-white/60 hover:text-white text-left">Features</button>
          <button onClick={() => scrollTo("pricing")} className="block py-2 text-white/60 hover:text-white text-left">Pricing</button>
          <Link href="/tools" className="block py-2 text-white/60 hover:text-white" onClick={() => setIsMenuOpen(false)}>Tools</Link>
          <Link href="/guides" className="block py-2 text-white/60 hover:text-white" onClick={() => setIsMenuOpen(false)}>Guides</Link>
          <Link href="/blog" className="block py-2 text-white/60 hover:text-white" onClick={() => setIsMenuOpen(false)}>Blog</Link>

          {isLoaded && (
            <>
              <SignedOut>
                <Link href={`/sign-in?redirect_url=${encodeURIComponent(currentUrl)}`} className="block py-2 text-white/60 hover:text-white" onClick={() => setIsMenuOpen(false)}>Sign In</Link>
              </SignedOut>

              <SignedIn>
                <div className="py-2 flex items-center justify-between border-t border-white/[0.06] mt-2 pt-4">
                  <div className="flex items-center gap-2 text-white/60">
                    <Coins className="h-4 w-4" />
                    <span className="text-sm">{videosRemaining} Credits</span>
                  </div>
                  <UserButton afterSignOutUrl="/" />
                </div>
              </SignedIn>
            </>
          )}
        </div>
      )}
    </nav>
  )
}

export function Header() {
  // Wrap the useSearchParams-dependent body in Suspense so every page that
  // includes <Header /> can be statically generated. During prerender the
  // fallback renders HeaderBody with currentUrl="/" (sign-in/sign-up links
  // lose the redirect_url param on static HTML only). After client hydration
  // the real currentUrl takes over.
  return (
    <Suspense fallback={<HeaderBody currentUrl="/" />}>
      <CurrentUrlReader onResolve={(currentUrl) => <HeaderBody currentUrl={currentUrl} />} />
    </Suspense>
  )
}
